import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

export function useArchPaymentInfo() {
    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_payment_info', null).catch(() => null)
        if (cached) {
            setRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_payment_info')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('updated_at', { ascending: false })
        if (err) {
            console.error('[archPaymentInfo] fetch error:', err.message)
            setError(err.message)
            if (!cached) setRows([])
        } else {
            setRows(data || [])
            await saveReferenceCache('arch_payment_info', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    /** Return active payment methods matching an office key (including 'both'). */
    const getPaymentInfos = useCallback((officeKey) => {
        if (!officeKey) return []
        return rows
            .filter(r => r.is_active !== false)
            .filter(r => r.office_key === officeKey || r.office_key === 'both')
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    }, [rows])

    /** Return all rows for admin list view. */
    const getAllPaymentInfos = useCallback(() => {
        return [...rows].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    }, [rows])

    const createPaymentInfo = useCallback(async (fields) => {
        const nextSort = rows.length > 0 ? Math.max(...rows.map(r => r.sort_order || 0)) + 1 : 0
        const payload = {
            office_key:              fields.office_key              || 'FL',
            bank_name:               fields.bank_name               || null,
            account_name:            fields.account_name            || null,
            account_number:          fields.account_number          || null,
            routing_number:          fields.routing_number          || null,
            swift_code:              fields.swift_code              || null,
            bank_address:            fields.bank_address            || null,
            additional_instructions: fields.additional_instructions || null,
            email:                   fields.email                   || null,
            card_note:               fields.card_note               || null,
            check_note:              fields.check_note              || null,
            sort_order:              nextSort,
            is_active:               fields.is_active !== false,
            updated_at:              new Date().toISOString(),
        }
        const { data, error: err } = await supabase
            .from('arch_payment_info')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('arch_payment_info', null).catch(() => {})
        return data
    }, [rows])

    const updatePaymentInfo = useCallback(async (id, fields) => {
        const payload = {
            office_key:              fields.office_key              || null,
            bank_name:               fields.bank_name               || null,
            account_name:            fields.account_name            || null,
            account_number:          fields.account_number          || null,
            routing_number:          fields.routing_number          || null,
            swift_code:              fields.swift_code              || null,
            bank_address:            fields.bank_address            || null,
            additional_instructions: fields.additional_instructions || null,
            email:                   fields.email                   || null,
            card_note:               fields.card_note               || null,
            check_note:              fields.check_note              || null,
            sort_order:              fields.sort_order,
            is_active:               fields.is_active,
            updated_at:              new Date().toISOString(),
        }
        // Remove undefined keys so Supabase doesn't overwrite with null
        Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k] })

        const { data, error: err } = await supabase
            .from('arch_payment_info')
            .update(payload)
            .eq('id', id)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => prev.map(r => r.id === id ? data : r).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('arch_payment_info', null).catch(() => {})
        return data
    }, [])

    const deletePaymentInfo = useCallback(async (id) => {
        const { error: err } = await supabase
            .from('arch_payment_info')
            .delete()
            .eq('id', id)
        if (err) throw new Error(err.message)
        setRows(prev => prev.filter(r => r.id !== id))
        await clearReferenceCache('arch_payment_info', null).catch(() => {})
    }, [])

    return { rows, loading, error, getPaymentInfos, getAllPaymentInfos, createPaymentInfo, updatePaymentInfo, deletePaymentInfo, reload: load }
}
